'use client'

import { createContext, useEffect, useState, ReactNode, JSX } from 'react'

import fetch_translation from '@/phrase/utils/fetchTranslations'

export interface TranslationContextProps {
  translations: {
    [key: string]: { message: string }
  }
  locale: string
  changeLanguage: (lang: string) => void
}

export const TranslationContext = createContext<TranslationContextProps>({
  translations: {},
  locale: 'en',
  changeLanguage: () => {},
})

interface TranslationProviderProps {
  children: ReactNode
  initialLocale?: string
}

export function TranslationProvider({
  children,
  initialLocale = 'en',
}: TranslationProviderProps): JSX.Element {
  const [locale, setLocale] = useState(initialLocale)
  const [loading, setLoading] = useState(true)

  const [translations, setTranslations] = useState<{
    [key: string]: { message: string }
  }>({})

  const changeLanguage = (lang: string) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)
    fetch('/api/set-locale', {
      method: 'POST',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale: lang }),
    })
      .then(() => setLocale(lang))
      .catch(() => {})
      .finally(() => clearTimeout(timeoutId))
  }

  useEffect(() => {
    // Ignore responses for a locale that is no longer current
    let cancelled = false
    fetch_translation(locale)
      .then((data) => {
        if (!cancelled) setTranslations(data)
      })
      .catch((err) => {
        console.error('Translation fetch error:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  if (loading) return <></>

  return (
    <TranslationContext.Provider
      value={{ translations, locale, changeLanguage }}
    >
      {children}
    </TranslationContext.Provider>
  )
}
